import { SectionShell } from "@/components/design-system/SectionShell";
import { LocationTag } from "@/components/ui/location-tag";
import { CategoryTag } from "@/components/ui/category-tag";
import { StatusChip } from "@/components/ui/status-chip";
import { TherapistAvatar } from "@/components/ui/therapist-avatar";

const code = `import { LocationTag } from "@/components/ui/location-tag";
import { CategoryTag } from "@/components/ui/category-tag";
import { StatusChip } from "@/components/ui/status-chip";
import { TherapistAvatar } from "@/components/ui/therapist-avatar";

// Location tag — teal pill with dot prefix
<LocationTag name="Prestwich" />
<LocationTag name="Hebden Bridge" />

// Category tag — sage pill for article categories
<CategoryTag label="Mental Health" />
<CategoryTag label="Therapist Resources" />

// Status chip — colour-coded for each record status
<StatusChip status="active" />
<StatusChip status="pending" />
<StatusChip status="inactive" />
<StatusChip status="published" />
<StatusChip status="draft" />

// Therapist avatar — teal circle with name initial
<TherapistAvatar name="Gisela Ramirez" size="sm" />
<TherapistAvatar name="Gisela Ramirez" size="md" />
<TherapistAvatar name="Gisela Ramirez" size="lg" />`;

export function SanctuaryComponentsSection() {
  return (
    <SectionShell
      id="sanctuary-components"
      title="Sanctuary components"
      description={
        <>
          Shared UI chips and decorators specific to Sanctuary Therapy Centre.
          Used across both the admin portal and the public website to represent
          locations, content categories, record statuses, and therapist identities.
        </>
      }
      whenToUse={
        <ul>
          <li>Use <code>&lt;LocationTag&gt;</code> wherever a location name appears inline (therapist cards, admin tables, listings).</li>
          <li>Use <code>&lt;CategoryTag&gt;</code> on article cards and in the articles admin table.</li>
          <li>Use <code>&lt;StatusChip&gt;</code> in every admin table that shows therapist, site, or article status.</li>
          <li>Use <code>&lt;TherapistAvatar&gt;</code> when no photo is available — it always shows the first initial.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Don't use <code>&lt;LocationTag&gt;</code> as a navigation element — it is decorative only.</li>
          <li>Don't use <code>&lt;StatusChip&gt;</code> for non-Sanctuary record types — use the standard <code>&lt;Badge&gt;</code> instead.</li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">Location tag</p>
            <div className="flex flex-wrap gap-2">
              <LocationTag name="Prestwich" />
              <LocationTag name="Hebden Bridge" />
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">Category tag</p>
            <div className="flex flex-wrap gap-2">
              <CategoryTag label="Mental Health" />
              <CategoryTag label="Therapist Resources" />
              <CategoryTag label="Wellbeing" />
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">Status chip</p>
            <div className="flex flex-wrap gap-2">
              <StatusChip status="active" />
              <StatusChip status="pending" />
              <StatusChip status="inactive" />
              <StatusChip status="published" />
              <StatusChip status="draft" />
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-muted">Therapist avatar</p>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center gap-1">
                <TherapistAvatar name="Gisela Ramirez" size="sm" />
                <span className="text-xs text-ink-muted">sm</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <TherapistAvatar name="Gisela Ramirez" size="md" />
                <span className="text-xs text-ink-muted">md</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <TherapistAvatar name="Gisela Ramirez" size="lg" />
                <span className="text-xs text-ink-muted">lg</span>
              </div>
            </div>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li><code>LocationTag</code>: <code>name</code> (string)</li>
          <li><code>CategoryTag</code>: <code>label</code> (string)</li>
          <li><code>StatusChip</code>: <code>status</code>: <code>active</code> | <code>pending</code> | <code>inactive</code> | <code>published</code> | <code>draft</code></li>
          <li><code>TherapistAvatar</code>: <code>name</code> (string), <code>size</code>: <code>sm</code> | <code>md</code> (default) | <code>lg</code></li>
        </ul>
      }
    />
  );
}
